import React, { useCallback, useRef, useReducer, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { DotsVerticalIcon } from './Icons';
import './VtonCard.scss';
import { useModelImage } from './ModelImageContext';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { ModelUploadType, VtonCardTriggerType, VtonImagePoseType } from './type';
import { GET_STYLE_VTON_IMAGES } from 'graph_ql/style_vton_image/queries';
import ClipLoader from 'react-spinners/ClipLoader';
import { RootState } from '../../redux/store';
import { LOCAL_FORAGE_KEY, LOCAL_STORAGE_KEY } from '../../utils/constant';
import localforage from 'localforage';
import { useLocalUser } from '../../hooks/useLocalUser';
import { useImageUpload } from './useImageUpload';
import { useTryOnGeneration } from './useTryOnGeneration';
import { vtonReducer, createInitialState } from './vtonReducer';
import { useAuthorize } from '../../hooks/useAuthorize';
import ZoomImage from 'component/zoom_image';
import { trackEvent, TRACK_EVENT } from '../../utils/analytics';

interface IVtonCard {
    height?: number | string | null;
    style?: any;
    item?: any;
    userDetail: any;
    canvasStyle?: any;
    secondImage?: string;
    className?: string;
    firstImageClassName?: string;
    secondImageClassname?: string;
    initialSliderPercentage?: number;
    slideMode?: 'hover' | 'drag';
    showHandlebar?: boolean;
    autoplay?: boolean;
    externalSliderPercentage?: number | null;
    setVtonSliderPercentage?: (value: number) => void;
    animationDuration?: number;
}
export const VtonCard = ({
    style = null,
    item = null,
    userDetail = null,
    canvasStyle = null,
    className,
    firstImageClassName,
    initialSliderPercentage = 99,
    slideMode = 'drag',
    showHandlebar = true,
    autoplay = false,
    externalSliderPercentage = null,
    setVtonSliderPercentage,
    animationDuration = 0.5,
}: IVtonCard) => {
    const authorize = useAuthorize();
    const { localUser } = useLocalUser();
    const {
        modelImageUrl: sharedModelImageUrl,
        setModelImageUrl: setSharedModelImageUrl,
        isUploading, // Global upload state for showing loading overlay in other cards
        setIsUploading,
        bodyFullFrontImage,
        setBodyFullFrontImage,
    } = useModelImage();
    const generatingUserImage = useSelector((state: RootState) => state.userDetail.generatingUserImage);
    const [showLargeImage, setShowLargeImage] = useState(false);

    // Component-specific loading states
    const [isCurrentlyUploading, setIsCurrentlyUploading] = useState(false);

    // Use reducer for consolidated state management
    const [state, dispatch_] = useReducer(
        vtonReducer,
        createInitialState(userDetail?.modelImageUrl, initialSliderPercentage),
    );

    const sliderRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const bodyThumbnailInputRef = useRef<HTMLInputElement>(null);
    const handlebarRef = useRef<HTMLDivElement>(null);

    // Custom hooks for image upload and try-on generation
    const { handleUpload, isGenerating } = useImageUpload({
        userId: userDetail?.id,
        setSharedModelImageUrl,
        onUploadComplete: (modelImageUrl) => {
            setIsCurrentlyUploading(false);
            dispatch_({ type: 'SET_TRY_ON_IMAGE', payload: modelImageUrl });
            generateTryOnImage({ modelImageUrl, forceUpdate: true });
        },
    });

    const { generateTryOnImage, loadingVtonImage } = useTryOnGeneration({
        style,
        item,
        userId: userDetail?.id,
        onCompleted: (url, poseType, taffiId) => {
            dispatch_({ type: 'SET_TRY_ON_IMAGE', payload: url });
            dispatch_({ type: 'ADD_POSE_TYPE_IMAGE', payload: { poseType, url, taffiId } });
        },
    });

    // Component-specific loading state - true if this component is loading
    const isComponentLoading = isCurrentlyUploading || isGenerating;

    // Determine loading state and message
    const isLoading =
        loadingVtonImage || isComponentLoading || (isUploading && !state.tryOnImage && !sharedModelImageUrl);
    const isLoadingMessage = isUploading && !isComponentLoading ? 'Uploading your photo...' : 'Styling you now…';
    const isLoadingDescription =
        isUploading && !isComponentLoading
            ? 'Processing your photo. This will take a moment.'
            : 'Creating your personalized look. This will take a few minutes.';

    const subDescription =
        isUploading && !isComponentLoading ? null : (
            <span className={'mt-15 font-14'}>
                Results are approximations and do not reflect actual product fit or appearance.
            </span>
        );

    // Load body full front image based on user authentication status
    useEffect(() => {
        if (sharedModelImageUrl) {
            dispatch_({ type: 'SET_TRY_ON_IMAGE', payload: sharedModelImageUrl });
        }
    }, [sharedModelImageUrl]);

    // Load body full front image based on user authentication status
    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        const loadBodyFullFrontImage = async () => {
            if (authorize.isAuthorize && userDetail?.bodyFullFrontImage) {
                // For authenticated users, use userDetail.bodyFullFrontImage
                if (isMounted) {
                    setBodyFullFrontImage(userDetail.bodyFullFrontImage);
                }
            } else if (!authorize.isAuthorize && userDetail?.id) {
                // For unauthorized users, try to get from localforage
                try {
                    const storedImage: any = await localforage.getItem(
                        LOCAL_FORAGE_KEY.LOCAL_USER_BODY_FULL_FRONT_IMAGE + userDetail.id,
                    );
                    if (isMounted && storedImage && typeof storedImage === 'object' && storedImage.data) {
                        // Build data URL from stored image data
                        const dataUrl = `data:${storedImage.mimeType};base64,${storedImage.data}`;
                        setBodyFullFrontImage(dataUrl);
                    }
                } catch (error) {
                    if (isMounted && !abortController.signal.aborted) {
                        console.error('Error loading body full front image from localforage:', error);
                    }
                }
            }
        };

        loadBodyFullFrontImage();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [authorize.isAuthorize, userDetail.id, userDetail.bodyFullFrontImage, setBodyFullFrontImage]);

    useQuery(GET_STYLE_VTON_IMAGES, {
        context: {
            allowAnonymousAuth: true,
        },
        variables: {
            input: { styleId: style?.id ?? style?.productId, userId: userDetail?.id },
        },
        skip: (!style?.id && !style?.productId) || !userDetail?.id,
        onCompleted(data) {
            if (data?.getStyleVtonImages && data.getStyleVtonImages.length > 0) {
                const newMap = new Map<VtonImagePoseType, { url: string; taffiId: string }>();
                data.getStyleVtonImages.forEach((img: any) => {
                    const taffiId = img.taffiId;
                    newMap.set(img.poseType, { url: img.url, taffiId });
                    if (img.poseType === VtonImagePoseType.FULL_FRONTAL_VIEW_HANDS_ON_HIPS) {
                        // set initial try-on image to full-frontal view with hands on hips
                        dispatch_({ type: 'SET_TRY_ON_IMAGE', payload: img.url });
                    }
                });
                // log all pose types in the map
                dispatch_({ type: 'SET_POSE_TYPE_MAP', payload: newMap });
            }
        },
        onError(error) {
            console.error('Error fetching style VTON images:', error);
        },
    });

    const currentPoseType = VtonImagePoseType.FULL_FRONTAL_VIEW_HANDS_ON_HIPS;

    // Track if slider is being controlled externally for animation
    const isExternalControl = useRef(false);

    // Memoize cursor style
    const cursorStyle = useMemo(() => {
        return slideMode === 'drag' ? (state.isDragging ? 'grabbing' : 'grab') : 'col-resize';
    }, [slideMode, state.isDragging]);

    // Memoize slider transition duration
    const sliderTransitionDuration = useMemo(() => {
        // When controlled externally, use animation
        if (isExternalControl.current) return animationDuration;
        // When dragging manually, no animation (instant update)
        if (state.isDragging) return 0;
        // Default: no animation
        return 0;
    }, [state.isDragging, animationDuration]);

    const handleUploadModelImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsCurrentlyUploading(true);
        setIsUploading(true); // Set global upload state for other cards
        try {
            const dataUrl = await handleUpload(file);
            if (dataUrl) {
                setBodyFullFrontImage(dataUrl);
                // Track vton_model_uploaded event for new upload
                trackEvent(TRACK_EVENT.VTON_MODEL_UPLOADED, {
                    product_taffi_id: item?.taffiId || item?.productTaffiId || style?.taffiId || style?.productTaffiId,
                    upload_type: ModelUploadType.NEW,
                });
            }
        } finally {
            setIsCurrentlyUploading(false);
            setIsUploading(false); // Clear global upload state
        }
    };

    const handleBodyImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsCurrentlyUploading(true);
        setIsUploading(true); // Set global upload state for other cards
        try {
            const dataUrl = await handleUpload(file);
            if (dataUrl) {
                setBodyFullFrontImage(dataUrl);
                // Track vton_model_uploaded event for replace upload
                trackEvent(TRACK_EVENT.VTON_MODEL_UPLOADED, {
                    product_taffi_id: item?.taffiId || item?.productTaffiId || style?.taffiId || style?.productTaffiId,
                    upload_type: ModelUploadType.REPLACE,
                });
            }
        } finally {
            setIsCurrentlyUploading(false);
            setIsUploading(false); // Clear global upload state
        }
    };

    function mouseLeaveHandler() {
        if (slideMode === 'hover') {
            dispatch_({ type: 'RESET_SLIDER', payload: initialSliderPercentage });
        }
        if (slideMode === 'drag') {
            dispatch_({ type: 'SET_IS_DRAGGING', payload: false });
            if (setVtonSliderPercentage) {
                setVtonSliderPercentage(state.sliderXPercent);
            }
        }
    }

    const handleStart = useCallback(() => {
        if (slideMode === 'drag') {
            dispatch_({ type: 'SET_IS_DRAGGING', payload: true });
            if (generatingUserImage || loadingVtonImage || isComponentLoading) {
                return;
            }
            if (!state.tryOnImage || loadingVtonImage) {
                return;
            }
            // check if try-on image exist for full frontal view
            const frontalPoseType = VtonImagePoseType.FULL_FRONTAL_VIEW_HANDS_ON_HIPS;
            if (state.mapPoseTypeToVtonImage.has(frontalPoseType)) {
                const vtonImage = state.mapPoseTypeToVtonImage.get(frontalPoseType);
                if (vtonImage) {
                    dispatch_({ type: 'SET_TRY_ON_IMAGE', payload: vtonImage.url });
                    return;
                }
            }
            // Check shared model image url first (highest priority)
            let modelImageUrl = sharedModelImageUrl;

            // Fallback to individual user model image if shared is not available
            if (!modelImageUrl) {
                modelImageUrl = userDetail.modelImageUrl;
            }
            if (!modelImageUrl) {
                // check if local user has model image url
                modelImageUrl = localUser?.modelImageUrl;
            }
            if (!modelImageUrl) {
                // get latest local user from localStorage
                const storedLocalUser = localStorage.getItem(LOCAL_STORAGE_KEY.LOCAL_USER_KEY);
                if (storedLocalUser) {
                    try {
                        const parsedLocalUser = JSON.parse(storedLocalUser);
                        modelImageUrl = parsedLocalUser?.modelImageUrl;
                    } catch (e) {
                        console.error('Error parsing local user from localStorage', e);
                    }
                }
            }
            if (!modelImageUrl) {
                console.warn('No model image url found');
                return;
            }
            // handle generate try-on image
            // if try-on image not exist, call the api to generate it
            // prepare data for api call, including style item images, user model image, pose type
            generateTryOnImage({ modelImageUrl });
        }
    }, [
        slideMode,
        generatingUserImage,
        loadingVtonImage,
        isComponentLoading,
        state.tryOnImage,
        state.mapPoseTypeToVtonImage,
        sharedModelImageUrl,
        generateTryOnImage,
        userDetail.modelImageUrl,
        localUser?.modelImageUrl,
    ]);

    const handleEnd = useCallback(() => {
        if (slideMode === 'drag') {
            if (state.sliderXPercent < 50 && !state.trackVtonScreenViewed) {
                dispatch_({ type: 'SET_TRACK_VTON_SCREEN_VIEWED', payload: true });
                // Get the taffiId for the current pose type
                const currentVtonImage = state.mapPoseTypeToVtonImage.get(currentPoseType);
                trackEvent(TRACK_EVENT.VTON_SCREEN_VIEWED, {
                    triggerEvent: VtonCardTriggerType.SLIDER,
                    product_taffi_id: item?.taffiId || item?.productTaffiId || style?.taffiId || style?.productTaffiId,
                    vton_taffi_id: currentVtonImage?.taffiId,
                });
            }
            if (state.sliderXPercent >= 50 && state.trackVtonScreenViewed) {
                // reset the tracking flag if slider moved back so that user can trigger again
                dispatch_({ type: 'SET_TRACK_VTON_SCREEN_VIEWED', payload: false });
            }
            dispatch_({ type: 'SET_IS_DRAGGING', payload: false });
            if (setVtonSliderPercentage) {
                setVtonSliderPercentage(state.sliderXPercent);
            }
        }
    }, [
        currentPoseType,
        item?.productTaffiId,
        item?.taffiId,
        setVtonSliderPercentage,
        slideMode,
        state.sliderXPercent,
        state.trackVtonScreenViewed,
        state.mapPoseTypeToVtonImage,
        style?.productTaffiId,
        style?.taffiId,
    ]);

    const handleMove = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            // ensure we have the element that the event was dispatched on
            const target = e.currentTarget as Element | null;
            if (!target) return;
            if (isExternalControl.current) {
                return;
            }

            if (slideMode === 'hover' || (slideMode === 'drag' && state.isDragging)) {
                const rect = target.getBoundingClientRect();
                const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
                const x = clientX - rect.left;
                const percent = (x / rect.width) * 100;
                requestAnimationFrame(() => {
                    dispatch_({ type: 'SET_SLIDER_PERCENT', payload: Math.max(0, Math.min(100, percent)) });
                });
            }
        },
        [slideMode, state.isDragging],
    );

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            // Only allow drag to start if clicking on the handlebar (DotsVerticalIcon area)
            if (handlebarRef.current?.contains(e.target as Node)) {
                handleStart();
            }
        },
        [handleStart],
    );
    const handleMouseUp = useCallback(() => handleEnd(), [handleEnd]);
    const handleMouseMove = useCallback((e: React.MouseEvent) => handleMove(e), [handleMove]);

    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            if (!autoplay && handlebarRef.current?.contains(e.target as Node)) {
                handleStart();
            }
        },
        [handleStart, autoplay],
    );

    const handleTouchEnd = useCallback(() => {
        if (!autoplay) {
            handleEnd();
        }
    }, [handleEnd, autoplay]);

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!autoplay && state.isDragging) {
                handleMove(e);
            }
        },
        [handleMove, autoplay, state.isDragging],
    );

    const handleShowLargeImage = useCallback(() => {
        setShowLargeImage(true);
    }, []);

    // Sync external slider percentage with internal state
    useEffect(() => {
        if (externalSliderPercentage !== null && externalSliderPercentage !== undefined) {
            isExternalControl.current = true;
            dispatch_({ type: 'SET_SLIDER_PERCENT', payload: externalSliderPercentage });
            dispatch_({ type: 'SET_IS_DRAGGING', payload: true });
            if (externalSliderPercentage === 0) {
                handleStart();
            }

            // Reset external control flag after animation completes
            const timer = setTimeout(() => {
                isExternalControl.current = false;
                // set isDragging to false after external control
                dispatch_({ type: 'SET_IS_DRAGGING', payload: false });
                if (externalSliderPercentage < 50 && !state.trackVtonScreenViewed) {
                    dispatch_({ type: 'SET_TRACK_VTON_SCREEN_VIEWED', payload: true });
                    // Get the taffiId for the current pose type
                    const currentVtonImage = state.mapPoseTypeToVtonImage.get(currentPoseType);
                    // Track vton_screen_viewed event when button is clicked
                    trackEvent(TRACK_EVENT.VTON_SCREEN_VIEWED, {
                        triggerEvent: VtonCardTriggerType.BUTTON_CLICK,
                        product_taffi_id:
                            item?.taffiId || item?.productTaffiId || style?.taffiId || style?.productTaffiId,
                        vton_taffi_id: currentVtonImage?.taffiId, // No VTON image generated yet
                    });
                }
                if (externalSliderPercentage >= 50 && state.trackVtonScreenViewed) {
                    // reset the tracking flag if slider moved back so that user can trigger again
                    dispatch_({ type: 'SET_TRACK_VTON_SCREEN_VIEWED', payload: false });
                }
            }, animationDuration * 1000);

            return () => clearTimeout(timer);
        }
    }, [
        externalSliderPercentage,
        handleStart,
        animationDuration,
        item?.taffiId,
        item?.productTaffiId,
        style?.taffiId,
        style?.productTaffiId,
        state.tryOnImage,
        state.mapPoseTypeToVtonImage,
        currentPoseType,
        state.trackVtonScreenViewed,
    ]);

    return (
        <div
            ref={sliderRef}
            className={clsx('swiper-no-swiping', 'vton-card-container', className)}
            onMouseMove={handleMouseMove}
            onMouseLeave={mouseLeaveHandler}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
        >
            <AnimatePresence initial={false}>
                <motion.div
                    className="vton-card-slider"
                    style={{
                        left: `${state.sliderXPercent}%`,
                        top: '0',
                        zIndex: 40,
                        transition: `left ${sliderTransitionDuration}s ease-out`,
                    }}
                    transition={{ duration: sliderTransitionDuration, ease: 'easeOut' }}
                >
                    {showHandlebar && (
                        <div
                            ref={handlebarRef}
                            className="vton-card-handlebar"
                            style={{
                                cursor: cursorStyle,
                            }}
                        >
                            <DotsVerticalIcon className="vton-card-dots-icon" />
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>
            <div
                className="vton-card-image-container"
                style={{
                    clipPath: canvasStyle ? `inset(0 ${100 - state.sliderXPercent}% 0 0)` : 'inset(0 0 0 0)',
                    transition: canvasStyle ? `clip-path ${sliderTransitionDuration}s ease-out` : 'none',
                    pointerEvents: canvasStyle ? 'auto' : 'none',
                }}
            >
                <AnimatePresence initial={false}>
                    {canvasStyle ? (
                        <motion.div
                            className={clsx('vton-card-first-image', firstImageClassName)}
                            style={{
                                clipPath: `inset(0 ${100 - state.sliderXPercent}% 0 0)`,
                                transition: `clip-path ${sliderTransitionDuration}s ease-out`,
                            }}
                            transition={{ duration: sliderTransitionDuration, ease: 'easeInOut' }}
                        >
                            {canvasStyle}
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </div>
            <AnimatePresence initial={false}>
                <motion.div className={'vton-card-second-image'}>
                    {state.tryOnImage || sharedModelImageUrl ? (
                        <div className={'try-on-container'}>
                            <img
                                alt={currentPoseType}
                                className={
                                    state.mapPoseTypeToVtonImage.has(currentPoseType)
                                        ? 'try-on-image-class'
                                        : 'model-image-class'
                                }
                                src={state.tryOnImage ?? sharedModelImageUrl}
                                onClick={handleShowLargeImage}
                                draggable={false}
                            />
                            {/*Pose type selector removed - only full frontal view enabled*/}
                            {/* Body full front image thumbnail */}
                            {bodyFullFrontImage && (
                                <div className="body-thumbnail-wrapper">
                                    <div
                                        className="body-thumbnail-container"
                                        onClick={() => bodyThumbnailInputRef.current?.click()}
                                        title="Upload new body image"
                                    >
                                        <img src={bodyFullFrontImage} alt="Body thumbnail" className="body-thumbnail" />
                                    </div>
                                    {/* Preview overlay - shown on hover via CSS */}
                                    <div className="body-thumbnail-preview">
                                        <img src={bodyFullFrontImage} alt="Body preview" />
                                    </div>
                                </div>
                            )}
                            <input
                                ref={bodyThumbnailInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleBodyImageUpload}
                            />
                        </div>
                    ) : (
                        <div className={clsx('upload-model-image-button')}>
                            <span>Try this style on you.</span>
                            <span>Upload a full-body photo to see how this style looks on you.</span>
                            {/* <a> tag with text "Upload Model Image" and onClick event to trigger file input */}
                            <a
                                href="/"
                                className="upload-model-image-label"
                                onClick={(e) => {
                                    e.preventDefault();
                                    fileInputRef.current?.click();
                                }}
                            >
                                Upload Your Photo
                            </a>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => handleUploadModelImage(e)}
                            />
                        </div>
                    )}
                    {isLoading ? (
                        <div className="vton-card-loading-overlay">
                            <span>{isLoadingMessage}</span>
                            <span className={'mb-15'}>{isLoadingDescription}</span>
                            <ClipLoader loading={true} size={50} color={'white'} />
                            {subDescription}
                        </div>
                    ) : null}
                    {showLargeImage && (
                        <ZoomImage
                            img={state.tryOnImage}
                            setShowImage={setShowLargeImage}
                            enableAnalytic={false}
                            height={'100%'}
                        />
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};
